import { useEffect } from 'react';

// Selector for the app's INNER scroll containers — pages that own their own
// overflow:auto scroll instead of scrolling the document. `.directory` is the
// dashboard / networks / gallery / stations browse pages (StationDirectory.css:
// height:100%; overflow-y:auto). Freezing <body> does NOT stop these, so we lock
// their overflow directly. (The chat message list is NOT here: it scrolls the
// document on mobile and uses inline styles + a ref, no stable class — the
// body-freeze covers it.)
const INNER_SCROLLERS = '.directory';

// ---------------------------------------------------------------------------
// Reference-counted page freeze (module-level, shared across ALL hook instances).
//
// Multiple overlays can be open at once — critically, AuthHeader (the nav-menu
// lock) renders INSIDE ChatInterface (the output-drawer lock), so both hooks are
// mounted on the station-chat page. If each instance saved/restored body styles
// independently, the freeze would stack: the 2nd lock would save the ALREADY
// position:fixed body as its "previous," and because React doesn't guarantee
// LIFO cleanup across components, the wrong instance could restore position:fixed
// as the "original" and leave the page permanently frozen.
//
// So the freeze is applied ONCE (0 -> 1) capturing the true pre-freeze styles,
// and undone ONCE (N -> 0). N overlays compose correctly regardless of open/close
// order. (The per-instance touchmove guard below is idempotent and stays
// per-instance — it's opt-in per overlay.)
// ---------------------------------------------------------------------------
let lockCount = 0;
let undoFreeze = null;

function acquireFreeze() {
  if (lockCount === 0) {
    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;
    const prevBody = {
      position: body.style.position, top: body.style.top, left: body.style.left,
      right: body.style.right, width: body.style.width, overflow: body.style.overflow,
    };
    const prevHtmlOverflow = html.style.overflow;

    // Freeze the document (works whether the body or the <html> element is the
    // scroll root): pin the body position:fixed at its offset AND clamp overflow
    // on both html and body for good measure. iOS Safari ignores overflow:hidden
    // on <body>, which is why the position:fixed pin is the real mechanism there.
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    // Lock the inner scroll containers currently in the DOM, remembering each
    // one's own inline overflow so we restore exactly (not blindly clear it).
    const inners = Array.from(document.querySelectorAll(INNER_SCROLLERS));
    const innerPrev = inners.map((el) => el.style.overflow);
    inners.forEach((el) => { el.style.overflow = 'hidden'; });

    undoFreeze = () => {
      html.style.overflow = prevHtmlOverflow;
      Object.assign(body.style, prevBody);
      inners.forEach((el, i) => { el.style.overflow = innerPrev[i]; });
      window.scrollTo(0, scrollY);
    };
  }
  lockCount += 1;
}

function releaseFreeze() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0 && undoFreeze) {
    undoFreeze();
    undoFreeze = null;
  }
}

// Freeze the page scroll while an overlay (mobile drawer, output panel, modal)
// covers it, so a touch / over-scroll on the overlay can't bleed through and
// scroll the view behind it. Pass `active` = the overlay-open boolean.
//
// The document freeze pins the body position:fixed at its offset (chat page: the
// body scrolls; the dashboard pages too) and locks any inner .directory scroller
// if one exists. It is REFERENCE-COUNTED (see above) so several
// overlays compose. A position:fixed overlay (the nav sheet / output drawer) is
// unaffected and its own inner container keeps scrolling normally.
//
// Extracted from ChatInterface's output-panel-scroll-lock. Note this is a
// behavioral SUPERSET of that original inline lock — it adds the html/body
// overflow clamp, the inner-scroller lock, ref-counting, and opt-in guardTouch —
// not a byte-for-byte move. Benign for ChatInterface (the additions only tighten
// the freeze it already relied on).
//
// opts.guardTouch (default false): also swallow document `touchmove` while
// locked, EXCEPT inside an element marked [data-scroll-lock-allow]. This is the
// bulletproof mobile lock (stops touch-scroll fall-through regardless of which
// element is the scroll root), but it disables ALL other touch scrolling, so
// only opt in for an overlay whose own scrollable regions are marked allow
// (the nav sheet). ChatInterface's drawer has many inner scroll regions and
// relies on the body-freeze alone, so it leaves this off.
export default function useBodyScrollLock(active, opts = {}) {
  const { guardTouch = false } = opts;
  useEffect(() => {
    if (!active) return undefined;
    acquireFreeze();

    // Belt-and-suspenders for touch devices (opt-in): a touch-drag that lands on
    // the fixed overlay (backdrop/sheet) otherwise scrolls the nearest scrollable
    // ancestor — the document — even with the freeze above on some engines.
    // Swallow touchmove unless it originates inside an element that opted in via
    // [data-scroll-lock-allow] (so a scrollable sheet/menu still works). Stays
    // per-instance (idempotent if two guards run) rather than ref-counted.
    let onTouchMove;
    if (guardTouch) {
      onTouchMove = (e) => {
        if (e.touches && e.touches.length > 1) return;         // pinch-zoom, leave alone
        if (e.target.closest && e.target.closest('[data-scroll-lock-allow]')) return;
        e.preventDefault();
      };
      document.addEventListener('touchmove', onTouchMove, { passive: false });
    }

    return () => {
      if (onTouchMove) document.removeEventListener('touchmove', onTouchMove);
      releaseFreeze();
    };
  }, [active, guardTouch]);
}
